import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import ApplicationModal from "@/components/ApplicationModal";
import PaymentModal from "@/components/PaymentModal";
import FilmMark from "@/components/FilmMark";
import { useLang } from "@/i18n/LanguageContext";
import { Heart, Building2, Award, UserPlus, Mail, ArrowRight, Palette, Users2, Megaphone, HandHeart, ChevronRight, Landmark, Bike, Trees, Anchor, Globe, Laptop, Code, Sparkles, Handshake, ScrollText, Gift, Ticket, Feather } from "lucide-react";
import UnescoMarquee from "@/components/UnescoMarquee";
import AdoptionSection from "@/components/AdoptionSection";
import SupportLanding from "@/components/SupportLanding";

const CONTACT_EMAIL = "arteurs.lecollectif@gmail.com";

const supports = [
  { Icon: Heart,      title: "Don",        body: "Un geste simple, un souffle qui compte. Même modeste, votre don prolonge une œuvre, un chemin, un artiste au bord de l'eau.", cta: "Offrir un don",       kind: "don",        anchor: "don" },
  { Icon: Building2,  title: "Sponsoring", body: "Associez le nom de votre entreprise à une aventure artistique de 750 km. Votre marque respire au rythme du canal — sous les yeux du monde.",         cta: "Nous accompagner",   kind: "sponsoring", anchor: "sponsoring" },
  { Icon: Award,      title: "Mécénat",    body: "Devenez complice d'une œuvre : un artiste, un pays, une escale. Un engagement long, un nom qui se grave — jusqu'à 60 % de déduction fiscale.",         cta: "Devenir mécène",    kind: "mecenat",    anchor: "mecenat" },
  { Icon: UserPlus,   title: "Adhésion",   body: "Franchissez la porte. Vernissages, ateliers, coulisses, rencontres : entrez dans la famille des Arteurs et faites route avec nous.",           cta: "Rejoindre la famille", kind: "adhesion",   anchor: "adhesion" },
  { Icon: Laptop,     title: "Bénévolat numérique", body: "Community management, rédaction, veille presse, montage vidéo, référencement, traduction : votre main derrière l'écran fait avancer le Fil, où que vous soyez.", cta: "Prêter une main", kind: "benevolat_numerique", anchor: "benevolat" },
];

const dossiers = [
  { title: "Sponsoring", body: "Associez votre marque à un événement artistique majeur sur le Canal du Midi. Visibilité, contreparties et impact territorial.", cta: "Recevoir le dossier sponsoring", kind: "sponsoring" },
  { title: "Mécénat",    body: "Soutenez durablement la création artistique avec des avantages fiscaux — réduction d'impôt jusqu'à 60 % pour les entreprises.", cta: "Recevoir le dossier mécénat",    kind: "mecenat" },
];

const avantages = [
  { Icon: Palette, title: "Exposez vos œuvres",  body: "Participez à nos expositions le long du canal et dans nos évènements." },
  { Icon: Users2,  title: "Réseau d'artistes",   body: "Intégrez une communauté dynamique d'artistes et artisans passionnés." },
  { Icon: Megaphone, title: "Visibilité",        body: "Bénéficiez d'une promotion de vos créations auprès d'un large public." },
  { Icon: HandHeart, title: "Engagement social", body: "Participez à des causes importantes comme Octobre Rose." },
];

const activites = [
  "Expositions le long du Canal du Midi",
  "Vente aux enchères des œuvres",
  "Évènements thématiques (Octobre Rose, etc.)",
  "Ateliers et rencontres artistiques",
  "Boutique en ligne collaborative",
  "Soutien à la création artistique",
];

const countries = [
  { flag: "🇫🇷", name: "France",   count: "+ artistes",  code: "fr" },
  { flag: "🇧🇪", name: "Belgique", count: "1 artiste",   code: "be" },
  { flag: "🇹🇬", name: "Togo",     count: "1 artiste",   code: "tg" },
  { flag: "🇨🇲", name: "Cameroun", count: "1 artiste",   code: "cm" },
];

// Country name → ISO 3166-1 alpha-2 (for flag emoji)
const COUNTRY_ISO = {
  "France": "FR", "Belgique": "BE", "Togo": "TG", "Cameroun": "CM",
  "Burkina Faso": "BF", "Bénin": "BJ", "Benin": "BJ", "Nigeria": "NG",
  "Gabon": "GA", "Portugal": "PT", "Sénégal": "SN", "Senegal": "SN",
  "Côte d'Ivoire": "CI", "Cote d'Ivoire": "CI", "Mali": "ML", "Niger": "NE",
  "Maroc": "MA", "Algérie": "DZ", "Tunisie": "TN", "Espagne": "ES",
  "Italie": "IT", "Allemagne": "DE", "Suisse": "CH", "Royaume-Uni": "GB",
  "Pays-Bas": "NL", "Luxembourg": "LU", "États-Unis": "US", "Canada": "CA",
  "Brésil": "BR", "Mexique": "MX", "Japon": "JP", "Chine": "CN",
  "Inde": "IN", "Ghana": "GH", "Kenya": "KE", "Afrique du Sud": "ZA",
  "Éthiopie": "ET", "Ethiopie": "ET", "Congo": "CG", "RD Congo": "CD",
};

function isoToFlag(iso) {
  if (!iso || iso.length !== 2) return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(A + iso.charCodeAt(0) - 65) + String.fromCodePoint(A + iso.charCodeAt(1) - 65);
}
function countryToFlag(name) {
  return isoToFlag(COUNTRY_ISO[name] || "");
}
function slugCode(name) {
  return (COUNTRY_ISO[name] || name).toLowerCase();
}

const unesco = [
  { Icon: Landmark, title: "Patrimoine UNESCO depuis 1996", body: "30 ans en 2026" },
  { Icon: Bike,     title: "Accessible à vélo, à pied, en bateau", body: "de Toulouse à Sète" },
  { Icon: Trees,    title: "42 000 platanes historiques", body: "plantés à l'origine · replantation en cours" },
  { Icon: Anchor,   title: "63 écluses · 328 ouvrages d'art", body: "génie civil exceptionnel" },
];

const participations = [
  {
    tag: "Sur sélection",
    title: "Œuvres in situ",
    body: "Installations pensées pour dialoguer avec le paysage, le patrimoine et les sites du canal.",
    bullets: ["Sélection curatoriale", "Projets temporaires et réversibles", "Accompagnement technique selon les projets"],
    cta: "Postuler · Œuvres in situ",
  },
  {
    tag: "Appel ouvert",
    title: "Œuvres sur Dibond",
    body: "Œuvres imprimées sur support Dibond et intégrées au parcours le long du canal.",
    bullets: ["Accessible à une large participation internationale", "Impression professionnelle", "Visibilité dans le parcours et sur les plateformes partenaires"],
    cta: "Candidater · Œuvres Dibond",
  },
];

function CountryCard({ country, index, countryToFlag }) {
  const [hovered, setHovered] = useState(false);
  const [idx, setIdx] = useState(0);
  const [validImages, setValidImages] = useState(() => country.images || []);
  const hasImages = validImages.length > 0;

  // Always auto-cycle when multiple images exist. Faster cadence on hover (desktop),
  // slower cadence otherwise (mobile / no hover). Keeps behaviour predictable & testable.
  useEffect(() => {
    if (validImages.length < 2) return;
    const delay = hovered ? 900 : 2800;
    const iv = setInterval(() => setIdx((i) => (i + 1) % validImages.length), delay);
    return () => clearInterval(iv);
  }, [hovered, validImages.length]);

  const onImgError = (src) => {
    setValidImages((cur) => {
      const next = cur.filter((s) => s !== src);
      if (idx >= next.length) setIdx(0);
      return next;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: (index % 8) * 0.05 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative border border-fg/15 min-h-[260px] overflow-hidden group cursor-pointer"
      data-testid={`country-${slugCode(country.name)}`}
    >
      {/* Rotating artwork background — always shown when available */}
      {hasImages && (
        <div className="absolute inset-0 pointer-events-none">
          {validImages.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt=""
              loading="lazy"
              onError={() => onImgError(src)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
              data-testid={i === idx ? `country-image-active-${slugCode(country.name)}` : undefined}
            />
          ))}
          <div className={`absolute inset-0 transition-colors duration-500 ${hovered ? "bg-fg/25" : "bg-fg/55"}`} />
        </div>
      )}

      {/* Content — always readable on top */}
      <div className={`relative flex flex-col justify-between h-full min-h-[260px] p-6 md:p-8 ${hasImages ? "text-bg" : "text-fg"}`}>
        {/* Flag chip — always visible, well-anchored top-left */}
        <div className="flex items-start justify-between gap-3">
          <span
            className="inline-flex items-center justify-center text-4xl md:text-5xl h-14 w-14 md:h-16 md:w-16 rounded-full bg-bg/95 border border-fg/15 shadow-lg shrink-0"
            aria-hidden
            data-testid={`flag-${slugCode(country.name)}`}
          >
            {countryToFlag(country.name)}
          </span>
          {hasImages && validImages.length > 1 && (
            <span
              className={`mt-2 font-mono text-[9px] tracking-[0.24em] uppercase ${hasImages ? "text-bg/85" : "text-fg/50"}`}
              data-testid={`country-counter-${slugCode(country.name)}`}
            >
              {idx + 1}/{validImages.length}
            </span>
          )}
        </div>
        <div>
          <div className="font-serif text-2xl md:text-3xl leading-tight tracking-tight drop-shadow-md">
            {country.name}
          </div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre mt-2 drop-shadow">
            {country.count} {country.count > 1 ? "artistes" : "artiste"}{hasImages ? ` · ${validImages.length} ${validImages.length > 1 ? "œuvres" : "œuvre"}` : ""}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function RejoindrePage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [countryStats, setCountryStats] = useState({ countries: [], total_countries: 0, target: 194 });
  const [modal, setModal] = useState({ open: false, kind: "artist", country: "" });
  const [payModal, setPayModal] = useState({ open: false, kind: "don" });
  const openApply = (kind, country = "") => setModal({ open: true, kind, country });
  const openPay = (kind) => setPayModal({ open: true, kind });

  useEffect(() => {
    api.get("/stats/countries").then((r) => setCountryStats(r.data)).catch(() => {});
    // Si arrivée depuis "J'adopte un artiste" : on scrolle vers la section chaleureuse
    // (au lieu d'ouvrir brutalement le modal — l'utilisateur voit d'abord la présentation)
    const params = new URLSearchParams(window.location.search);
    if (params.get("adopt") === "1") {
      // léger délai pour laisser le hero se poser
      setTimeout(() => {
        const el = document.getElementById("adopte");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }
  }, []);
  return (
    <main className="pt-32 pb-20" data-testid="rejoindre-page">
      <FilmMark />
      <UnescoMarquee />
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "Support Us · Les Arteurs" : "Je participe · Les Arteurs"}</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tight mt-8">
          {en ? <>Join<br/><em className="italic">Les Arteurs</em>,<br/>join <em className="italic">the Thread</em>.</> : <>Rejoignez<br/><em className="italic">Les Arteurs</em>,<br/>rejoignez <em className="italic">le Fil</em>.</>}
        </h1>
        <p className="mt-10 font-mono text-[11px] tracking-[0.24em] uppercase text-fg/60 max-w-2xl">
          {en ? "Walls keep us from seeing? Let's break them together — to open doors." : "Les murs empêchent de voir ? Cassons-les ensemble — mais pour ouvrir des portes."}
        </p>
      </section>

      {/* Supports · les 5 gestes — cartes qui scrollent vers les landings détaillés ci-dessous */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="rejoindre-supports">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "How to support us?" : "Comment nous soutenir ?"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>Five <em className="italic">gestures</em>.</> : <>Cinq <em className="italic">gestes</em>.</>}
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? "Every contribution — big or small — becomes a link in the Thread. Choose the form that suits you: we welcome you personally, with the care of a first exchange." : "Chaque contribution — grande ou petite — devient un maillon du Fil. Choisissez la forme qui vous ressemble : nous vous accueillons personnellement, avec le soin d'un premier échange."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {supports.map((s, i) => (
            <motion.a
              key={s.title}
              href={`#${s.anchor}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className="group relative border border-fg/15 p-8 md:p-10 flex flex-col justify-between min-h-[280px] hover:bg-fg hover:text-bg transition-colors duration-500 text-left cursor-pointer"
              data-testid={`support-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div>
                <s.Icon size={26} strokeWidth={1.2} className="text-cinabre" />
                <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mt-6">{s.title}</h3>
                <p className="mt-4 leading-relaxed opacity-80">{s.body}</p>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre group-hover:text-bg">
                {s.cta} <ArrowRight size={12} />
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* === Les 5 landings chaleureuses === */}

      {/* 1. Adoption d'un artiste (composant dédié — profil riche) */}
      <AdoptionSection onOpen={() => openApply("adoption_artiste")} />

      {/* 2. Don */}
      <SupportLanding
        id="don"
        tag={en ? "§ A breath that counts" : "§ Un souffle qui compte"}
        title={en ? <>Make a <em className="italic">donation.</em></> : <>Faire un <em className="italic">don.</em></>}
        quote={en ? "A breath that counts. Even modest, a donation extends an artwork, a path, an artist by the water." : "Un souffle qui compte. Même modeste, un don prolonge une œuvre, un chemin, un artiste au bord de l'eau."}
        intro={en ? "You freely choose the amount. Tax receipt: 66% deduction for individuals, 60% for companies (Aillagon Law)." : "Vous choisissez librement le montant. Reçu fiscal : 66 % de déduction pour les particuliers, 60 % pour les entreprises (loi Aillagon)."}
        micro={en ? "Secure online payment by Stripe — bank card, in under 2 minutes." : "Paiement en ligne sécurisé par Stripe — carte bancaire, en moins de 2 minutes."}
        promises={en ? [
          { Icon: Heart, title: "The free gesture", body: "€1, €10, €100 — every donation counts. You set the pace and the amount." },
          { Icon: Gift, title: "Tax receipt", body: "Tax certificate sent by email after your donation. Real tax savings." },
          { Icon: Sparkles, title: "A link in the Thread", body: "Your name appears (if you wish) on the donors' wall — discreet, sober, eternal." },
        ] : [
          { Icon: Heart,     title: "Le geste libre", body: "1 €, 10 €, 100 € — chaque don compte. Vous fixez le rythme et le montant." },
          { Icon: Gift,      title: "Reçu fiscal", body: "Attestation fiscale envoyée par email après votre don. Économie d'impôt réelle." },
          { Icon: Sparkles,  title: "Un maillon dans le Fil", body: "Votre nom apparaît (si vous le souhaitez) sur le mur des donateurs — discret, sobre, éternel." },
        ]}
        ctaLabel={en ? "Make a donation" : "Je fais un don"}
        ctaMicro={en ? "2 min · secure Stripe payment" : "2 min · paiement sécurisé Stripe"}
        onOpen={() => openPay("don")}
      />
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto mt-8">
        <Link
          to="/donateurs"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre hover:gap-3 transition-all"
          data-testid="link-donors-wall"
        >
          {en ? "See the donors' wall" : "Voir le mur des donateurs"} <ArrowRight size={13} />
        </Link>
      </div>

      {/* 3. Sponsoring */}
      <SupportLanding
        id="sponsoring"
        tag={en ? "§ Your brand, our current" : "§ Votre marque, notre courant"}
        title={en ? <>Sponsor <em className="italic">the Thread.</em></> : <>Sponsoriser <em className="italic">le Fil.</em></>}
        quote={en ? "Associate your brand with a 750 km artistic adventure, before the eyes of the world — from the Montagne Noire to the Atlantic." : "Associez votre marque à une aventure artistique de 750 km, sous les yeux du monde — de la Montagne Noire à l'Atlantique."}
        intro={en ? "Event sponsorship, site sponsorship, artist sponsorship: three possible grammars, adapted to your sector, your values, your territories." : "Sponsoring événementiel, sponsoring de site, sponsoring d'artiste : trois grammaires possibles, adaptées à votre secteur, vos valeurs, vos territoires."}
        micro={en ? "We come back to you within 5 working days with a personalised pack: visibility (website, social media, banners, catalogue), benefits (opening invitations, private tours, a workshop with an artist), and a quantified impact report." : "Nous revenons vers vous sous 5 jours ouvrés avec un dossier nominatif : visibilité (site, réseaux, bâches, catalogue), contreparties (invitations vernissages, visites privées, atelier avec un·e artiste), et retour d'impact chiffré."}
        promises={en ? [
          { Icon: Megaphone, title: "Multi-month visibility", body: "6 sites, 3 months of exhibition (Oct-Dec 2026), national and international press: your name breathes to the canal's rhythm." },
          { Icon: Handshake, title: "Activated territories", body: "Occitanie, Hérault, Aude, Haute-Garonne. Visibility rooted in the canal's 84 towns." },
          { Icon: Ticket, title: "Tangible benefits", body: "Invitations, studio visits, speeches at openings, personalised catalogue, engraved mention." },
        ] : [
          { Icon: Megaphone, title: "Visibilité pluri-mois", body: "6 sites, 3 mois d'exposition (oct-déc 2026), presse nationale et internationale : votre nom respire au rythme du canal." },
          { Icon: Handshake, title: "Territoires activés", body: "Occitanie, Hérault, Aude, Haute-Garonne. Une visibilité ancrée dans les 84 communes du canal." },
          { Icon: Ticket,    title: "Contreparties concrètes", body: "Invitations, visites d'atelier, prises de parole aux vernissages, catalogue nominatif, mention gravée." },
        ]}
        ctaLabel={en ? "I sponsor" : "Je sponsorise"}
        ctaMicro={en ? "3 min · reply within 5 working days" : "3 min · réponse sous 5 jours ouvrés"}
        onOpen={() => openApply("sponsoring")}
      />

      {/* 4. Mécénat */}
      <SupportLanding
        id="mecenat"
        tag={en ? "§ A name that is engraved" : "§ Nom qui se grave"}
        title={en ? <>Become a <em className="italic">patron.</em></> : <>Devenir <em className="italic">mécène.</em></>}
        quote={en ? "A long commitment, a name that is engraved. Becoming a patron means adopting an artwork, an artist, a country, a stop — and carrying it through." : "Un engagement long, un nom qui se grave. Devenir mécène, c'est adopter une œuvre, un artiste, un pays, une escale — et la porter jusqu'au bout."}
        intro={en ? "Aillagon Law: 60% tax deduction for companies, 66% for individuals. Benefits possible up to 25% of the donation." : "Loi Aillagon : 60 % de déduction fiscale pour les entreprises, 66 % pour les particuliers. Contreparties possibles jusqu'à 25 % du don."}
        micro={en ? "We send you a personalised pack within 5 working days, and offer a meeting with the artistic direction to build your chapter of the Thread together." : "Nous vous adressons un dossier nominatif sous 5 jours ouvrés, et proposons un rendez-vous avec la direction artistique pour construire ensemble votre chapitre du Fil."}
        promises={en ? [
          { Icon: Award, title: "60% tax deduction", body: "Aillagon Law (companies) or 66% (individual Cerfa). Your commitment counts twice." },
          { Icon: ScrollText, title: "A chapter in your name", body: "A stop, an artist, a residency, a lock house — you choose the chapter. It will carry your trace." },
          { Icon: Landmark, title: "A UNESCO legacy", body: "Your name associated with the Canal du Midi's 30th World Heritage anniversary. Museum archiving." },
        ] : [
          { Icon: Award,    title: "60 % de déduction fiscale", body: "Loi Aillagon (entreprises) ou 66 % (Cerfa particulier). Votre engagement compte deux fois." },
          { Icon: ScrollText, title: "Un chapitre à votre nom", body: "Une escale, un artiste, une résidence, une éclusière — vous choisissez le chapitre. Il portera votre trace." },
          { Icon: Landmark, title: "Un legs UNESCO", body: "Votre nom associé au 30ᵉ anniversaire du Canal du Midi au Patrimoine mondial. Un archivage muséal." },
        ]}
        ctaLabel={en ? "I commit" : "Je m'engage"}
        ctaMicro={en ? "Personalised pack within 5 working days" : "Dossier nominatif sous 5 jours ouvrés"}
        onOpen={() => openApply("mecenat")}
      />

      {/* 5. Adhésion */}
      <SupportLanding
        id="adhesion"
        tag={en ? "§ The Arteurs' door" : "§ La porte des Arteurs"}
        title={en ? <>Walk through the <em className="italic">door.</em></> : <>Franchir la <em className="italic">porte.</em></>}
        quote={en ? "Openings, workshops, backstage, encounters — membership means joining the Arteurs family and travelling with us." : "Vernissages, ateliers, coulisses, rencontres — l'adhésion, c'est entrer dans la famille des Arteurs et faire route avec nous."}
        intro={en ? "A personalised membership card, sent within 15 days. Free annual fee from €30 (individuals) — you choose your commitment." : "Une carte de membre nominative, envoyée sous 15 jours. Cotisation annuelle libre à partir de 30 € (particuliers) — vous choisissez votre engagement."}
        micro={en ? 'Would you like to apply to exhibit an artwork? Not here — head to "Apply".' : "Vous souhaitez candidater pour exposer une œuvre ? Ce n'est pas ici — c'est sur « Je postule »."}
        promises={en ? [
          { Icon: UserPlus, title: "Openings & backstage", body: "All the 2026 inaugurations, studio encounters, private tours with the artists." },
          { Icon: Users2, title: "The Arteurs family", body: "Join a francophone and international collective · 30 artists in 2025 → 194 countries by 2030." },
          { Icon: Feather, title: "A voice at the AGM", body: "A deliberative voice at the general assembly, access to the accounts, participation in curatorial choices." },
        ] : [
          { Icon: UserPlus, title: "Vernissages & coulisses", body: "Toutes les inaugurations 2026, les rencontres d'atelier, les visites privées avec les artistes." },
          { Icon: Users2,   title: "La famille des Arteurs", body: "Rejoindre un collectif francophone et international · 30 artistes en 2025 → 194 pays à horizon 2030." },
          { Icon: Feather,  title: "Une voix aux AG", body: "Voix délibérative en assemblée générale, accès aux comptes, participation aux choix curatoriaux." },
        ]}
        ctaLabel={en ? "I join" : "J'adhère"}
        ctaMicro={en ? "Secure Stripe online payment · membership card within 15 days" : "Paiement en ligne sécurisé Stripe · carte de membre sous 15 jours"}
        onOpen={() => openPay("adhesion")}
      />

      {/* 6. Bénévolat numérique */}
      <SupportLanding
        id="benevolat"
        tag={en ? "§ A hand behind the screen" : "§ Une main derrière l'écran"}
        title={en ? <>Digital <em className="italic">volunteering.</em></> : <>Bénévolat <em className="italic">numérique.</em></>}
        quote={en ? "Community management, writing, press monitoring, video editing, SEO, translation: your hand behind the screen moves the Thread forward, wherever you are." : "Community management, rédaction, veille presse, montage vidéo, référencement, traduction : votre main derrière l'écran fait avancer le Fil, où que vous soyez."}
        intro={en ? "The Thread is also woven online. We are looking for kind hands for short missions (2-3 h/week) or longer ones (project lead)." : "Le Fil se tisse aussi en ligne. Nous cherchons des mains bienveillantes pour des missions courtes (2-3 h/semaine) ou plus longues (chef·fe de projet)."}
        micro={en ? "Open missions: Instagram, TikTok, LinkedIn, Substack newsletter, Reels editing, FR→EN/ES/PT translation, press monitoring, SEO/Wikipedia, subtitling." : "Missions ouvertes : Instagram, TikTok, LinkedIn, newsletter Substack, montage Reels, traduction FR→EN/ES/PT, veille presse, référencement SEO/Wikipedia, sous-titrage."}
        promises={en ? [
          { Icon: Laptop, title: "Remote, at your own pace", body: "From home, in Ouagadougou, Lisbon, Montreal or Toulouse. Occasional video meetings." },
          { Icon: Code, title: "Varied missions", body: "Community management, writing, video editing, SEO, translation, subtitling." },
          { Icon: Sparkles, title: "A certificate & a network", body: "Volunteering certificate, mention in the credits, the Arteurs' international network." },
        ] : [
          { Icon: Laptop,   title: "À distance, à votre rythme", body: "Depuis chez vous, à Ouagadougou, Lisbonne, Montréal ou Toulouse. Réunions visio ponctuelles." },
          { Icon: Code,     title: "Missions variées", body: "Community management, rédaction, montage vidéo, référencement, traduction, sous-titrage." },
          { Icon: Sparkles, title: "Un attestation & un réseau", body: "Attestation de bénévolat, mention dans les crédits, réseau international des Arteurs." },
        ]}
        ctaLabel={en ? "I sign up" : "Je m'inscris"}
        ctaMicro={en ? "3 min · reply within 5 working days" : "3 min · réponse sous 5 jours ouvrés"}
        onOpen={() => openApply("benevolat_numerique")}
      />

      {/* Déjà international · Appel à participation */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="appel-participation">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Call for participation · Already international" : "Appel à participation · Déjà international"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>The first <em className="italic">countries</em>.</> : <>Les premiers <em className="italic">pays</em>.</>}
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? <>Every new application opens a border, draws a bridge, widens the map. Goal: <span className="text-cinabre">194 artists · 194 UNESCO countries</span> by 2030.</> : <>Chaque nouvelle candidature ouvre une frontière, dessine un pont, agrandit la carte. Objectif : <span className="text-cinabre">194 artistes · 194 pays UNESCO</span> à l'horizon 2030.</>}
            </p>
            <div className="mt-6 flex items-baseline gap-4">
              <div className="font-serif text-6xl md:text-7xl leading-none tracking-tight text-cinabre" data-testid="country-progress-count">
                {countryStats.total_countries}
              </div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
                {en ? <>countries<br/>already represented<br/>out of 194 UNESCO</> : <>pays<br/>déjà représentés<br/>sur 194 UNESCO</>}
              </div>
            </div>
            <div className="mt-4 h-[3px] bg-fg/10 relative overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${Math.min(100, (countryStats.total_countries / countryStats.target) * 100)}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.65, 0, 0.35, 1] }}
                className="absolute inset-y-0 left-0 bg-cinabre"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="countries-grid">
          {countryStats.countries.map((c, i) => (
            <CountryCard key={c.name} country={c} index={i} slugCode={slugCode} countryToFlag={countryToFlag} />
          ))}

          {/* Empty slot inviting the next country */}
          <motion.button
            type="button"
            onClick={() => openApply("first-of-country")}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="border border-dashed border-cinabre/60 p-6 md:p-8 flex flex-col justify-between min-h-[220px] hover:bg-cinabre hover:text-bg hover:border-cinabre transition-colors duration-500 group text-left"
            data-testid="country-empty-slot"
          >
            <div className="text-6xl md:text-7xl leading-none opacity-40 group-hover:opacity-100" aria-hidden>🏳️</div>
            <div>
              <div className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mt-4 text-cinabre group-hover:text-bg">{en ? "Your country?" : "Votre pays ?"}</div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 group-hover:text-bg/80 mt-2 flex items-center gap-2">
                {en ? "Be the first" : "Soyez le premier"} <ArrowRight size={12} />
              </div>
            </div>
          </motion.button>
        </div>

        <button
          type="button"
          onClick={() => openApply("first-of-country")}
          className="mt-8 inline-flex items-center gap-3 btn-editorial"
          data-testid="cta-first-country"
        >
          <Globe size={14} /> {en ? "Your country is not represented yet? Be the first" : "Votre pays n'est pas encore représenté ? Soyez le premier"} <ArrowRight size={14} />
        </button>
      </section>

      {/* Pourquoi le Canal du Midi */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="pourquoi-canal">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-6">
            <div className="meta-label text-cinabre">§ {en ? "World Heritage" : "Patrimoine mondial"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>Why the<br/><em className="italic">Canal du Midi</em>?</> : <>Pourquoi le<br/><em className="italic">Canal du Midi</em> ?</>}
            </h2>
          </div>
          <div className="md:col-span-6 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? <>Conceived by Pierre-Paul Riquet in the 17th century, the Canal du Midi links the Atlantic to the Mediterranean over 240 km. An engineering masterpiece, it has been a UNESCO World Heritage Site since <span className="text-cinabre">1996</span>. In <span className="text-cinabre">2026</span>, we celebrate 30 years of inscription.</> : <>Imaginé par Pierre-Paul Riquet au XVIIᵉ siècle, le Canal du Midi relie l'Atlantique à la Méditerranée sur 240 km. Chef-d'œuvre d'ingénierie, il est inscrit au patrimoine mondial de l'UNESCO depuis <span className="text-cinabre">1996</span>. En <span className="text-cinabre">2026</span>, nous célébrons ses 30 ans d'inscription.</>}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-fg/15 border border-fg/15">
          {unesco.map((u, i) => (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              className="bg-bg p-8 min-h-[200px] flex flex-col justify-between"
            >
              <u.Icon size={26} strokeWidth={1.2} className="text-cinabre" />
              <div>
                <div className="font-serif text-xl md:text-2xl leading-tight tracking-tight mt-6">{u.title}</div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 mt-2">{u.body}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Deux formes de participation */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="deux-formes">
        <div className="mb-14">
          <div className="meta-label text-cinabre">§ Participation</div>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
            {en ? <>Two forms of <em className="italic">participation</em>.</> : <>Deux formes de <em className="italic">participation</em>.</>}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {participations.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className="border border-fg/15 p-8 md:p-10 flex flex-col justify-between min-h-[420px] group hover:bg-fg hover:text-bg transition-colors duration-500"
              data-testid={`participation-${i}`}
            >
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">§ {p.tag}</div>
                <h3 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mt-4">{p.title}</h3>
                <p className="mt-6 leading-relaxed opacity-90">{p.body}</p>
                <ul className="mt-8 space-y-3">
                  {p.bullets.map(b => (
                    <li key={b} className="flex items-start gap-3">
                      <ChevronRight size={14} className="text-cinabre mt-1 shrink-0" />
                      <span className="leading-relaxed opacity-90">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => openApply(i === 0 ? "in-situ" : "dibond")}
                className="mt-10 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre group-hover:text-bg link-underline text-left"
              >
                <Mail size={12} /> {p.cta} <ArrowRight size={12} />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dossiers Entreprises */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="rejoindre-dossiers">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Corporate partnership" : "Partenariat Entreprises"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <><em className="italic">Patronage</em> &<br/>Sponsorship packs.</> : <>Dossiers <em className="italic">Mécénat</em><br/>& Sponsoring.</>}
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? "Would you like to support Canal'Art 2026? Write to us — we will send you our full pack: terms, benefits, tax advantages, and a tailored first exchange." : "Vous souhaitez soutenir Canal'Art 2026 ? Écrivez-nous — nous vous adressons notre dossier complet : modalités, contreparties, avantages fiscaux, et un premier échange sur mesure."}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {dossiers.map((d, i) => (
            <motion.button
              key={d.title}
              type="button"
              onClick={() => openApply(d.kind)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className="group border border-fg/15 p-8 md:p-12 flex flex-col justify-between min-h-[240px] hover:bg-cinabre hover:text-bg hover:border-cinabre transition-colors duration-500 text-left"
              data-testid={`dossier-${d.title.toLowerCase()}`}
            >
              <div>
                <div className="meta-label text-cinabre group-hover:text-bg">§ Dossier</div>
                <h3 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mt-4">{d.title}</h3>
                <p className="mt-6 leading-relaxed opacity-90 max-w-lg">{d.body}</p>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase">
                <Mail size={14} /> {d.cta} <ArrowRight size={14} />
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-8 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
          {en ? "Direct contact: " : "Contact direct : "}<a href={`mailto:${CONTACT_EMAIL}`} className="link-underline text-fg">{CONTACT_EMAIL}</a> · 40 Grande Rue, 31620 Castelnau d'Estrétefonds
        </div>
      </section>

      {/* Avantages membres */}
      <section className="mt-24 md:mt-32 bg-fg text-bg py-24 md:py-32 px-6 md:px-12" data-testid="rejoindre-avantages">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-7">
              <div className="meta-label text-cinabre">§ {en ? "Become a member" : "Devenir membre"}</div>
              <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
                {en ? <>Member<br/><em className="italic">benefits</em>.</> : <>Avantages<br/>des <em className="italic">membres</em>.</>}
              </h2>
            </div>
            <div className="md:col-span-5 self-end">
              <p className="text-bg/80 leading-relaxed text-lg">
                {en ? "Become a member of a dynamic association committed to contemporary art — from Castelnau d'Estrétefonds to the Mediterranean." : "Devenez membre d'une association dynamique et engagée pour l'art contemporain — de Castelnau d'Estrétefonds à la Méditerranée."}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bg/15 border border-bg/15">
            {avantages.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="bg-fg p-8"
                data-testid={`avantage-${i}`}
              >
                <a.Icon size={22} strokeWidth={1.2} className="text-cinabre" />
                <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mt-6">{a.title}</h3>
                <p className="mt-3 leading-relaxed text-bg/75">{a.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Activités */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="rejoindre-activites">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="meta-label text-cinabre">§ {en ? "Our activities" : "Nos activités"}</div>
            <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mt-6">
              {en ? <>What drives <em className="italic">us</em>.</> : <>Ce qui nous <em className="italic">anime</em>.</>}
            </h2>
            <p className="mt-8 text-fg/75 leading-relaxed text-lg max-w-md">
              {en ? "As a member or supporter, take part in all our actions — on the canal, online, in galleries and museums." : "En tant que membre ou soutien, participez à toutes nos actions — sur le canal, en ligne, dans les galeries et les musées."}
            </p>
          </div>
          <ul className="md:col-span-7 space-y-4">
            {activites.map((a, i) => (
              <motion.li
                key={a}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                className="border-b border-fg/15 pb-4 flex items-center gap-4 group"
              >
                <ChevronRight size={16} className="text-cinabre shrink-0" />
                <span className="font-serif text-2xl md:text-3xl leading-tight tracking-tight">{a}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Newsletter + CTA */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="rejoindre-cta">
        <div className="grid md:grid-cols-12 gap-10 border border-fg/15 p-8 md:p-16">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Stay in the thread" : "Restez dans le fil"}</div>
            <h3 className="font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight mt-4">
              {en ? <>Walls keep us from seeing?<br/>Let's break them.</> : <>Les murs empêchent de voir ?<br/>Cassons-les.</>}
            </h3>
            <p className="mt-6 text-fg/75 leading-relaxed max-w-xl">
              {en ? "Subscribe to our newsletter to receive all our news, upcoming events and calls for artists." : "Inscrivez-vous à notre newsletter pour recevoir toutes nos actualités, nos prochains évènements et nos appels à artistes."}
            </p>
          </div>
          <div className="md:col-span-5 self-center flex flex-col gap-3">
            <Link to="/#newsletter" className="btn-editorial" data-testid="rejoindre-newsletter">
              {en ? "Subscribe to the newsletter" : "S'inscrire à la newsletter"} <ArrowRight size={14} />
            </Link>
            <button onClick={() => openPay("adhesion")} className="btn-editorial ghost" data-testid="rejoindre-adhesion">
              {en ? "Join now" : "Adhérer maintenant"} <UserPlus size={14} />
            </button>
          </div>
        </div>
      </section>

      <ApplicationModal
        open={modal.open}
        kind={modal.kind}
        defaultCountry={modal.country}
        onClose={() => setModal({ ...modal, open: false })}
      />
      <PaymentModal
        open={payModal.open}
        kind={payModal.kind}
        onClose={() => setPayModal({ ...payModal, open: false })}
      />
    </main>
  );
}
